<!--
  Global Search Component

  2025-06-04: Updated to use isAllowed and hasAbility methods from common_functions.js for role-based filtering
  2025-05-29: Replaced hardcoded shortcuts with dynamic menu-based shortcuts, added filtering and breadcrumb paths
-->

<template>
  <v-autocomplete
    v-model="model"
    hide-details
    flat
    solo
    dense
    no-filter
    outlined
    clearable
    :items="combinedResults"
    :search-input.sync="searchValue"
    :loading="isLoading"
    item-text="displayName"
    return-object
    class="search-input"
    :placeholder="
      isMobile
        ? $t('Search...')
        : $t('Search listings, reservations, and navigation')
    "
    @click="handleClick"
    @change="handleSelection"
    @update:search-input="debouncedSearch"
  >
    <template #item="{ item }">
      <!-- Divider for Categories -->
      <div
        v-if="item.isDivider"
        :class="`w-100 text-center font-weight-medium mb-4 py-1 text-body-2 white--text ${
          item.color || 'info'
        } darken-1`"
      >
        {{ item.label }}
      </div>

      <!-- Shortcut Item -->
      <div
        v-else-if="item.type === 'shortcut'"
        class="pa-3 shortcut-item d-flex align-center"
      >
        <v-icon :color="item.iconColor || 'primary'" class="mr-3">
          {{ item.icon }}
        </v-icon>
        <div class="flex-grow-1">
          <div class="font-weight-medium">{{ item.title }}</div>
          <div
            v-if="item.breadcrumb"
            class="text-caption primary--text text--darken-2"
          >
            {{ item.breadcrumb }}
          </div>
          <div v-else class="text-caption grey--text text--darken-2">
            {{ item.description }}
          </div>
        </div>
      </div>

      <!-- Listing Item -->
      <div
        v-else-if="item.type === 'listing'"
        :class="`d-flex align-center pa-3 listing-item ${
          item.lightText ? 'grey--text' : ''
        }`"
      >
        <v-avatar tile rounded class="mr-3">
          <v-img
            :src="item.image || item.picture || placeholderImg"
            class="rounded"
          />
        </v-avatar>
        <div>
          <div class="d-flex align-center">
            <div class="font-weight-medium">{{ item.nickname }}</div>
            <v-chip
              v-if="item.listed"
              label
              class="mx-2"
              x-small
              color="green lighten-3"
            >
              {{ $t('Listed') }}
            </v-chip>
            <v-chip
              v-if="!item.listed"
              label
              class="mx-2"
              x-small
              color="red lighten-3"
            >
              {{ $t('UnListed') }}
            </v-chip>
            <v-chip
              v-if="!item.active"
              label
              class="mx-2"
              x-small
              color="grey lighten-2"
            >
              {{ $t('Disabled') }}
            </v-chip>
            <v-chip
              v-if="item.second_guesty_id"
              label
              class="mx-2"
              x-small
              color="blue lighten-4"
            >
              {{ $t('Dup') }}
            </v-chip>
            <v-chip
              v-if="!item.second_guesty_id"
              label
              class="mx-2"
              x-small
              color="purple lighten-4"
            >
              {{ $t('Main') }}
            </v-chip>
            <v-chip
              v-if="item.is_multi_unit"
              label
              class="mx-2"
              x-small
              color="cyan lighten-4"
            >
              {{ $t('Multi') }}
            </v-chip>
            <v-chip
              v-if="item.listing_type === 'hotel'"
              label
              class="mx-2"
              x-small
              color="purple lighten-4"
            >
              {{ $t('Complex') }}
            </v-chip>
            <v-chip
              v-if="item.stage === 'setup'"
              label
              class="mx-2"
              x-small
              color="orange lighten-4"
            >
              {{ $t('Setup') }}
            </v-chip>
          </div>
          <div class="text-caption">{{ item.title }}</div>
          <div
            class="text-caption d-flex align-center grey--text font-weight-medium"
          >
            <span>{{ item.beds }}</span>
            <v-icon class="mx-1" small color="grey">mdi-bed</v-icon>
            <span class="mx-1">•</span>
            <span>{{ item.baths }}</span>
            <v-icon class="mx-1" small color="grey">mdi-shower</v-icon>
          </div>
        </div>
      </div>

      <!-- Reservation Item -->
      <div
        v-else-if="item.type === 'reservation'"
        class="pa-3 reservation-item"
      >
        <div class="font-weight-medium mb-1">
          {{ item.guest_name }}
          <span v-if="item.confirmation_code" class="text-caption font-italic">
            ({{ item.confirmation_code }})
          </span>
        </div>
        <div class="text-caption mb-1">
          {{
            parseDateTZ(
              item.reservation_check_in,
              'MMM DD, YYYY',
              item.timezone
            )
          }}
          -
          {{
            parseDateTZ(
              item.reservation_check_out,
              'MMM DD, YYYY',
              item.timezone
            )
          }}
        </div>
        <div class="text-caption d-flex align-center">
          <v-chip
            small
            label
            class="mr-2"
            :color="resStatusColor(item.reservation_status)"
          >
            {{ item.reservation_status }}
          </v-chip>
          <span>{{ item.listing }}</span>
        </div>
      </div>
    </template>
  </v-autocomplete>
</template>

<script>
import { mapActions } from 'vuex'
import CommonFunctions from 'components/mixins/common_functions'
import debounce from 'lodash/debounce'
import placeholderImg from '@/assets/images/home.png'
import DeviceMixin from 'components/mixins/device-mixin'
import { menuItemsPro } from 'components/menu/menu-items'
import { getV2BaseUrl } from '@/utils/v2-base-url'

export default {
  name: 'GlobalSearch',
  mixins: [CommonFunctions, DeviceMixin],

  data() {
    return {
      model: null,
      searchValue: null,
      listings: [],
      reservations: [],
      isLoading: false,
      placeholderImg,
      debouncedSearch: debounce(this.performSearch, 500),
    }
  },
  computed: {
    menuShortcuts() {
      return this.flattenMenuItems(menuItemsPro)
    },

    filteredShortcuts() {
      if (!this.searchValue || this.searchValue.length < 2) {
        return []
      }

      const searchTerm = this.searchValue.toLowerCase()
      return this.menuShortcuts.filter(
        shortcut =>
          shortcut.title.toLowerCase().includes(searchTerm) ||
          shortcut.description.toLowerCase().includes(searchTerm) ||
          shortcut.breadcrumb.toLowerCase().includes(searchTerm) ||
          shortcut.tabs?.some(tab => tab.toLowerCase().includes(searchTerm))
      )
    },

    combinedResults() {
      const results = []

      // Add shortcuts (always show, filtered when searching)
      if (this.filteredShortcuts.length > 0) {
        results.push({
          isDivider: true,
          label:
            this.searchValue && this.searchValue.length >= 2
              ? this.$t('Navigation')
              : this.$t('Quick Access'),
          color: 'primary',
        })
        results.push(...this.filteredShortcuts)
      }

      if (this.listings.length) {
        // Categorize listings
        const categories = {
          main: [],
          multiUnits: [],
          units: [],
          dups: [],
          setup: [],
          disabled: [],
        }

        this.listings.forEach(listing => {
          if (!listing.active) {
            categories.disabled.push({
              ...listing,
              type: 'listing',
              displayName: listing.nickname,
              lightText: true,
            })
          } else if (listing.is_multi_unit) {
            categories.multiUnits.push({
              ...listing,
              type: 'listing',
              displayName: listing.nickname,
            })
          } else if (listing.container_multi_id) {
            categories.units.push({
              ...listing,
              type: 'listing',
              displayName: listing.nickname,
            })
          } else if (listing.second_guesty_id) {
            categories.dups.push({
              ...listing,
              type: 'listing',
              displayName: listing.nickname,
              lightText: true,
            })
          } else if (listing.stage === 'setup') {
            categories.setup.push({
              ...listing,
              type: 'listing',
              displayName: listing.nickname,
              lightText: true,
            })
          } else {
            categories.main.push({
              ...listing,
              type: 'listing',
              displayName: listing.nickname,
            })
          }
        })

        // Add main listings
        if (categories.main.length) {
          results.push({ isDivider: true, label: this.$t('Listings') })
          results.push(...categories.main)
        }

        // Add multi units
        if (categories.multiUnits.length) {
          results.push({
            isDivider: true,
            label: this.$t('Multi units'),
            color: 'green',
          })
          results.push(...categories.multiUnits)
        }

        // Add units
        if (categories.units.length) {
          results.push({
            isDivider: true,
            label: this.$t('Units'),
            color: 'teal',
          })
          results.push(...categories.units)
        }

        // Add duplicates
        if (categories.dups.length) {
          results.push({
            isDivider: true,
            label: this.$t('Duplicates'),
            color: 'amber',
          })
          results.push(...categories.dups)
        }

        // Add setups
        if (categories.setup.length) {
          results.push({
            isDivider: true,
            label: this.$t('Setups'),
            color: 'indigo',
          })
          results.push(...categories.setup)
        }

        // Add disabled
        if (categories.disabled.length) {
          results.push({
            isDivider: true,
            label: this.$t('Disabled'),
            color: 'blue-grey',
          })
          results.push(...categories.disabled)
        }
      }

      // Add reservations at the end
      if (this.reservations.length) {
        results.push({
          isDivider: true,
          label: this.$t('Reservations'),
        })
        results.push(
          ...this.reservations.map(reservation => ({
            ...reservation,
            type: 'reservation',
            displayName: `${reservation.guest_name} - ${reservation.confirmation_code}`,
          }))
        )
      }

      return results
    },
  },

  methods: {
    ...mapActions('reservation', ['searchReservations']),

    hasAccess(item) {
      if (!item.allowed_roles && !item.abilities) {
        return true
      }
      const hasRoleAccess = this.isAllowed(item.allowed_roles)
      const hasAbilityAccess = item.abilities
        ? this.hasAbility(item.abilities)
        : true

      if (item.abilityOrRole) {
        return hasRoleAccess || hasAbilityAccess
      }
      return hasRoleAccess && hasAbilityAccess
    },

    flattenMenuItems(menuItems, parentPath = '') {
      const shortcuts = []

      menuItems.forEach(item => {
        // Skip items user doesn't have access to
        if (!this.hasAccess(item)) {
          return
        }

        const currentPath = parentPath
          ? `${parentPath} > ${item.text}`
          : item.text

        // Add the item itself if it has a value (route) or value_v2
        if (item.value || item.value_v2) {
          const isExternal = !item.value && item.value_v2
          const route = item.value || this.getV2Url(item.value_v2)
          shortcuts.push({
            type: 'shortcut',
            displayName: item.text,
            title: item.text,
            description: item.description || '',
            icon: item.icon || 'mdi-menu',
            iconColor: this.getIconColor(item.icon),
            route,
            isExternal,
            breadcrumb: item.tabs ? item.tabs.join(', ') : parentPath,
            tabs: item.tabs || [],
            searchText: `${item.text} ${
              item.description || ''
            } ${currentPath}`.toLowerCase(),
          })
        }

        // Recursively add submenu items (they will be filtered individually)
        if (item.submenu && item.submenu.length > 0) {
          shortcuts.push(...this.flattenMenuItems(item.submenu, currentPath))
        }
      })

      return shortcuts
    },

    getIconColor(icon) {
      // Map specific icons to colors
      const iconColors = {
        'mdi-view-dashboard': 'blue',
        'mdi-calendar-multiselect': 'green',
        'mdi-account-outline': 'purple',
        'mdi-home-variant-outline': 'orange',
        'mdi-briefcase-outline': 'indigo',
        'mdi-account-hard-hat-outline': 'brown',
        'mdi-office-building-cog-outline': 'teal',
        'mdi-router-wireless': 'cyan',
        'mdi-warehouse': 'grey',
        'mdi-briefcase-check-outline': 'pink',
        'mdi-sitemap-outline': 'deep-purple',
        'mdi-robot': 'amber',
        'mdi-cog-outline': 'blue-grey',
        'mdi-emoticon-outline': 'light-green',
        'mdi-book-open-outline': 'blue',
        'mdi-access-point-network': 'deep-orange',
        'mdi-star-outline': 'yellow darken-3',
        'mdi-currency-usd': 'green',
        'mdi-account-tie': 'purple',
        'mdi-web': 'indigo',
        'mdi-cash-register': 'green',
        'mdi-bank': 'blue',
        'mdi-book-open-variant': 'brown',
        'mdi-cash-multiple': 'green',
        'mdi-file-document-outline': 'grey',
        'mdi-chart-line': 'orange',
        'mdi-tools': 'red',
        'mdi-pound': 'purple',
      }
      return iconColors[icon] || 'primary'
    },

    handleClick() {
      this.listings = this.$store.state.listingsIndex
    },

    async performSearch() {
      if (!this.searchValue || this.searchValue.length < 2) {
        this.listings = []
        this.reservations = []
        return
      }

      this.isLoading = true

      try {
        // Search listings from store
        this.listings = this.$store.state.listingsIndex.filter(
          listing =>
            listing.nickname
              ?.toLowerCase()
              .includes(this.searchValue.toLowerCase()) ||
            listing.title
              ?.toLowerCase()
              .includes(this.searchValue.toLowerCase()) ||
            listing.airbnb_id
              ?.toLowerCase()
              .includes(this.searchValue.toLowerCase()) ||
            listing.guesty_id
              ?.toLowerCase()
              .includes(this.searchValue.toLowerCase())
        )

        // Search reservations
        const reservations = await this.searchReservations({
          term: this.searchValue,
        })
        this.reservations = reservations || []
      } catch (error) {
        console.error('Search failed:', error)
      } finally {
        this.isLoading = false
      }
    },

    getV2Url(path) {
      return `${getV2BaseUrl()}${path}`
    },

    handleSelection(item) {
      if (!item) return

      if (item.type === 'shortcut') {
        if (item.isExternal) {
          window.location.href = item.route
        } else {
          this.$router.push(item.route)
        }
      } else if (item.type === 'listing') {
        const path = this.isInvestor
          ? `/dashboard/property/${item.id}`
          : `/dashboard/edit/${item.id}`
        this.$router.push(path)
      } else if (item.type === 'reservation') {
        this.$router.push(
          `/dashboard/reservation/${item.reservation_guesty_id}`
        )
      }

      // Clear the input and model after navigation
      this.$nextTick(() => {
        this.model = null
        this.searchValue = null
      })
    },

    resStatusColor(status) {
      const colors = {
        confirmed: 'green lighten-4',
        canceled: 'red lighten-4',
        inquiry: 'blue lighten-4',
        declined: 'grey lighten-4',
      }
      return colors[status?.toLowerCase()] || 'grey lighten-4'
    },
  },
}
</script>

<style lang="scss" scoped>
.search-input {
  border-radius: 10px;
}

.shortcut-item {
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--v-grey-lighten4);
  }
}

:deep(.v-list-item) {
  padding: 0;
}
:deep(.v-input__slot) {
  background-color: var(--v-secondary-lighten3) !important;
}
:deep(.v-input__slot fieldset) {
  border-color: var(--v-secondary-lighten) !important;
}
</style>
